import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// No extra parameters: one array for every render, so the callbacks keep their identity.
const NONE = Object.freeze([]);

/*
 * Which sheet is open, kept in the address: ?<param>=<name>, plus any `extra`
 * parameters that say what it is open on (Home's ?tool=thermal&for=<isolate>;
 * Field Mode's camera, ?sheet=gallery | hub | pro). So the phone's own Back,
 * the Capacitor shell's Back and an in-page Back close the sheet instead of
 * leaving the page, and a link or a reload opens it again (2026-10-21;
 * docs/FIELD_AI_VISION_SPEC.md section 9; docs/UI_HUBS_AND_GESTURES_PLAN.md
 * section 2).
 *
 *   const { sheet, open, close } = useAddressSheet('tool', ['thermal'], { extra: ['for'] });
 *   open('thermal', { for: id });
 *
 * One sheet at a time: opening a sheet pushes a history entry; opening
 * another (or the same one on something else) while one is open replaces it,
 * so a single Back always returns to the page. Closing goes back through the
 * entry the sheet pushed; a sheet that came with the address (a link, a
 * reload, a Back into it) has none to go back through, and the address simply
 * loses its parameters.
 *
 * `allowed` lists the names that open a sheet; any other value in the address
 * opens nothing. It may instead be a function of the value, for a sheet open
 * on something with no fixed list of names: the chat drawer's ?chat=inbox or
 * ?chat=<channel id> (components/chat/ChatDrawer.js); keep such a function's
 * identity from render to render.
 *
 * Whether this hook pushed the entry is kept in its history state, under
 * `stateKey` (default `<param>SheetPushed`), beside whatever else the entry's
 * state holds.
 */
export default function useAddressSheet(param, allowed, { extra = NONE, stateKey = `${param}SheetPushed` } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const accepts = useMemo(() => (typeof allowed === 'function' ? (name) => !!name && !!allowed(name)
    : (name) => Array.isArray(allowed) && allowed.includes(name)), [allowed]);
  const extras = useMemo(() => (Array.isArray(extra) ? extra : []), [extra]);
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const asked = query.get(param);
  const sheet = accepts(asked) ? asked : null;
  const values = useMemo(() => Object.fromEntries(extras.map((key) => [key, query.get(key) || ''])), [extras, query]);
  const state = location.state && typeof location.state === 'object' ? location.state : null;
  const pushed = !!state?.[stateKey];

  /** Everything the entry's state held but our own flag, and the flag as given. */
  const stateWith = useCallback((flag) => {
    const rest = { ...(state || {}) };
    delete rest[stateKey];
    return flag ? { ...rest, [stateKey]: true } : rest;
  }, [state, stateKey]);

  const open = useCallback((name, given = {}) => {
    if (!accepts(name)) return;
    const wanted = Object.fromEntries(extras.map((key) => [key, given?.[key] == null ? '' : String(given[key])]));
    if (name === sheet && extras.every((key) => wanted[key] === values[key])) return;
    const search = new URLSearchParams(location.search);
    search.set(param, name);
    extras.forEach((key) => {
      if (wanted[key]) search.set(key, wanted[key]);
      else search.delete(key);
    });
    navigate({ pathname: location.pathname, search: `?${search}` }, {
      replace: !!sheet,
      // Replacing keeps whether the entry was ours to go back through.
      state: stateWith(sheet ? pushed : true),
    });
  }, [accepts, extras, sheet, values, pushed, param, location.pathname, location.search, navigate, stateWith]);

  const close = useCallback(() => {
    if (!sheet) return;
    if (pushed) {
      navigate(-1);
      return;
    }
    const search = new URLSearchParams(location.search);
    search.delete(param);
    extras.forEach((key) => search.delete(key));
    const rest = search.toString();
    navigate({ pathname: location.pathname, search: rest ? `?${rest}` : '' }, { replace: true, state: stateWith(false) });
  }, [sheet, pushed, param, extras, location.pathname, location.search, navigate, stateWith]);

  return { sheet, values, open, close };
}
